// @ts-nocheck
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BsDaterangepickerDirective } from 'ngx-bootstrap/datepicker';


/**
 * Date range reactive form control
 * Work by ngx-bootstrap/datepicker
 * https://valor-software.com/ngx-bootstrap/#/datepicker#usage
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-calendar-range-control',
    templateUrl: './calendar-range-control.component.html',
    styleUrls: ['./calendar-range-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CalendarRangeControlComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class CalendarRangeControlComponent implements AfterViewInit, OnDestroy {
    /**
     * Label text
     */
    @Input() public label: string;
    /**
     * Placeholder value
     */
    @Input() public placeholder = '';
    /**
     * Min-date
     */
    @Input() public minDate: Date;
    /**
     * Max-date
     */
    @Input() public maxDate: Date;
    /**
     * Horizontal align control and label
     */
    @Input() public horizontal = false;
    /**
     * Angular reactive form control name
     */
    @Input() public formControlName: string;
    /**
     * Date format
     */
    @Input() public dateFormat: string;
    /**
     * Control size
     */
    @Input() public size?: 'sm' | 'lg' = null;
    /**
     * #Ref object
     */
    @ViewChild('calendar', { static: true }) public inputRef: BsDaterangepickerDirective;
    /**
     * Changed flag
     */
    public changed: boolean;
    /**
     * Disable flag
     */
    public isDisabled: boolean;
    /**
     * FormControl object
     */
    private control: AbstractControl = null;
    /**
     * Destroy var for unsubscribe
     */
    private readonly destroy$: Subject<unknown> = new Subject<unknown>();

    constructor(private injector: Injector, private cd: ChangeDetectorRef) {
    }

    public ngAfterViewInit(): void {
        const ngControl: NgControl = this.injector.get<any>(NgControl as any, null);

        if (ngControl) {
            this.control = ngControl.control as FormControl;
            this.control.statusChanges
                .pipe(
                    distinctUntilChanged((a, b) => a === b),
                    takeUntil(this.destroy$),
                )
                .subscribe((status) => {
                    this.cd.markForCheck();
                });
        } else {
            console.warn('Control is not defined', ngControl);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    public patchValue(value: Date[]): void {
        if (value) {
            this.inputRef.bsValue = value;
        }

        this.cd.markForCheck();
    }

    public onChange(value): void {
        this.change(value);
    }

    public onBlur(value): void {
        this.changed = true;
        this.touchedChange('');
    }

    public onKeyUp(value): void {
        this.change(value.target.value);
    }

    public change(value): void {
        this.cd.markForCheck();
        this.propagateChange(value);
    }

    // propagate changes into the custom form control
    public propagateChange = (_: string): void => {
    };

    public touchedChange = (_: string): void => {
    };

    // From ControlValueAccessor interface
    public writeValue(value: Date[]): void {
        if (value !== value) {
            this.changed = true;
        }

        this.patchValue(value);
    }

    // From ControlValueAccessor interface
    public registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    // From ControlValueAccessor interface
    public registerOnTouched(fn: any): void {
        this.touchedChange = fn;
    }
}
